import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import adminFetch from '../../services/adminFetch';
import './StreamAdminPage.css';

const CREATION_STATUS_OPTIONS = [
  { value: 'completed', label: '已完成' },
  { value: 'completed_with_errors', label: '已完成（有错误）' },
  { value: 'creating', label: '创建中' },
  { value: 'planning', label: '规划中' },
  { value: 'generating_basic', label: '生成基础内容' },
  { value: 'active', label: '进行中' },
  { value: 'pending_selection', label: '待选择' },
  { value: 'failed', label: '失败' },
  { value: 'cancelled', label: '已取消' },
];

const clampVolume = (value, fallback = 100) => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return fallback;
  return Math.max(0, Math.min(100, parsed));
};

const getVideoAudioConfig = (video = {}) => ({
  video_volume: clampVolume(video.video_volume, 100),
  background_music_volume: clampVolume(video.background_music_volume, 30),
});

const getApiErrorMessage = async (response, fallback) => {
  const body = await response.json().catch(() => ({}));
  return typeof body.detail === 'string' ? body.detail : fallback;
};

const mergeVideo = (videos, nextVideo) => (
  videos.map((video) => (video.id === nextVideo.id ? nextVideo : video))
);

const StreamAdminPage = () => {
  const { id: streamId } = useParams();

  const [streamInfo, setStreamInfo] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingInfo, setSavingInfo] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null);
  const [editingOrder, setEditingOrder] = useState(false);
  const [orderValue, setOrderValue] = useState('');
  const [uploadingMusic, setUploadingMusic] = useState(false);
  const [savingAudioVideoId, setSavingAudioVideoId] = useState(null);
  const [savingStreamMusic, setSavingStreamMusic] = useState(false);

  const updateStreamInfoState = useCallback((patch) => {
    setStreamInfo((prev) => ({ ...(prev || {}), ...patch }));
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminFetch(`/admin/streams/${streamId}/videos`);
      if (!response.ok) throw new Error('获取数据失败');
      const data = await response.json();
      setStreamInfo(data.stream_info || { id: data.stream_id, name: data.stream_name });
      setVideos(data.videos || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [streamId]);

  const refreshVideosOnly = useCallback(async () => {
    const response = await adminFetch(`/admin/streams/${streamId}/videos`);
    if (!response.ok) throw new Error(await getApiErrorMessage(response, '获取数据失败'));
    const data = await response.json();
    setStreamInfo(data.stream_info || { id: data.stream_id, name: data.stream_name });
    setVideos(data.videos || []);
  }, [streamId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const introVideo = useMemo(() => videos.find(v => v.video_category === 'intro'), [videos]);
  const idleVideo = useMemo(() => {
    const idle = videos.find(v => v.video_category === 'idle');
    if (idle) return idle;
    if (streamInfo?.default_video_id) return videos.find(v => v.id === streamInfo.default_video_id);
    return null;
  }, [videos, streamInfo]);
  const interactionVideos = useMemo(() =>
    videos
      .filter(v => !['intro', 'idle'].includes(v.video_category))
      .sort((a, b) => (b.priority || 0) - (a.priority || 0)),
    [videos]
  );

  // ---------- Upload video ----------
  const handleUploadVideo = useCallback(async (category, file) => {
    if (!file) return;
    setUploading(category);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('video_category', category);
      formData.append('interaction_type', category);
      const res = await adminFetch(`/admin/streams/${streamId}/upload-video`, { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '上传失败');
      await refreshVideosOnly();
    } catch (err) { alert('上传失败: ' + err.message); }
    finally { setUploading(null); }
  }, [streamId, refreshVideosOnly]);

  // ---------- Set as idle (default) ----------
  const handleSetIdleVideo = useCallback(async (videoId) => {
    try {
      await adminFetch(`/admin/videos/${videoId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ video_category: 'idle' })
      });
      await adminFetch(`/admin/streams/${streamId}/default-video?video_id=${videoId}`, { method: 'PUT' });
      await fetchData();
    } catch (err) { alert('设置失败: ' + err.message); }
  }, [streamId, fetchData]);

  // ---------- Delete video ----------
  const handleDeleteVideo = useCallback(async (video) => {
    if (!window.confirm(`确定删除「${video.interaction_label || video.interaction_type}」？`)) return;
    try {
      await adminFetch(`/admin/videos/${video.id}`, { method: 'DELETE' });
      await fetchData();
    } catch (err) { alert('删除失败: ' + err.message); }
  }, [fetchData]);

  // ---------- Update video ----------
  const handleUpdateVideo = useCallback(async (videoId, updateData) => {
    try {
      const res = await adminFetch(`/admin/videos/${videoId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, '更新失败'));
      const data = await res.json();
      if (data?.video) {
        setVideos((prev) => mergeVideo(prev, data.video));
      } else {
        await refreshVideosOnly();
      }
      setEditingVideo(null);
      return true;
    } catch (err) { alert('更新失败: ' + err.message); return false; }
  }, [refreshVideosOnly]);

  const handleSaveAudioConfig = useCallback(async (video, draft) => {
    setSavingAudioVideoId(video.id);
    const ok = await handleUpdateVideo(video.id, {
      video_volume: clampVolume(draft.video_volume, 100),
      background_music_enabled: streamInfo?.background_music_id ? 1 : 0,
      background_music_volume: clampVolume(draft.background_music_volume, 30),
    });
    setSavingAudioVideoId(null);
    return ok;
  }, [handleUpdateVideo, streamInfo?.background_music_id]);

  const handleUploadBackgroundMusic = useCallback(async (file) => {
    if (!file) return;
    setUploadingMusic(true);
    setSavingStreamMusic(true);
    try {
      const formData = new FormData();
      formData.append('music_file', file);
      formData.append('title', file.name.replace(/\.[^.]+$/, ''));
      const res = await adminFetch(`/admin/streams/${streamId}/background-music/upload`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, '上传并绑定背景音乐失败'));
      await refreshVideosOnly();
    } catch (err) {
      alert('上传并绑定背景音乐失败: ' + err.message);
    } finally {
      setUploadingMusic(false);
      setSavingStreamMusic(false);
    }
  }, [refreshVideosOnly, streamId]);

  const handleClearBackgroundMusic = useCallback(async () => {
    if (!streamId) return;
    setSavingStreamMusic(true);
    try {
      const res = await adminFetch(`/admin/streams/${streamId}/background-music`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ background_music_id: null }),
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, '取消绑定背景音乐失败'));
      await refreshVideosOnly();
    } catch (err) {
      alert('取消绑定背景音乐失败: ' + err.message);
    } finally {
      setSavingStreamMusic(false);
    }
  }, [refreshVideosOnly, streamId]);

  const selectedStreamMusic = useMemo(() => {
    if (!streamInfo?.background_music_id) return null;
    return {
      id: streamInfo.background_music_id,
      title: streamInfo.background_music_title || '背景音乐',
      filename: streamInfo.background_music_filename,
      tos_url: streamInfo.background_music_tos_url,
      audio_url: streamInfo.background_music_audio_url,
    };
  }, [
    streamInfo?.background_music_audio_url,
    streamInfo?.background_music_filename,
    streamInfo?.background_music_id,
    streamInfo?.background_music_title,
    streamInfo?.background_music_tos_url,
  ]);

  // ---------- Replace video file ----------
  const handleReplaceVideoFile = useCallback(async (videoId, file) => {
    if (!file) return;
    setUploading(`replace_${videoId}`);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await adminFetch(`/admin/videos/${videoId}/replace-file`, { method: 'PUT', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '替换失败');
      await refreshVideosOnly();
    } catch (err) { alert('替换视频失败: ' + err.message); }
    finally { setUploading(null); }
  }, [refreshVideosOnly]);

  // ---------- Upload action image ----------
  const handleUploadActionImage = useCallback(async (videoId, file) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('video_id', videoId);
      const res = await adminFetch(`/admin/streams/${streamId}/upload-action-image`, { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '上传失败');
      await handleUpdateVideo(videoId, { action_image: data.filename, tos_action_image: data.url });
    } catch (err) { alert('上传图片失败: ' + err.message); }
  }, [streamId, handleUpdateVideo]);

  // ---------- Drag reorder ----------
  const dragOverRef = useRef(null);

  const handleDragStart = useCallback((videoId) => { setDraggingId(videoId); }, []);

  const handleDragOver = useCallback((e, videoId) => {
    e.preventDefault();
    dragOverRef.current = videoId;
  }, []);

  const handleDrop = useCallback(async () => {
    if (draggingId == null || dragOverRef.current == null || draggingId === dragOverRef.current) {
      setDraggingId(null);
      return;
    }
    const list = [...interactionVideos];
    const fromIdx = list.findIndex(v => v.id === draggingId);
    const toIdx = list.findIndex(v => v.id === dragOverRef.current);
    if (fromIdx < 0 || toIdx < 0) { setDraggingId(null); return; }
    const [moved] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, moved);
    setDraggingId(null);
    dragOverRef.current = null;

    try {
      await adminFetch(`/admin/streams/${streamId}/reorder-videos`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(list.map(v => v.id))
      });
      await fetchData();
    } catch (err) { alert('排序失败: ' + err.message); }
  }, [draggingId, interactionVideos, streamId, fetchData]);

  // ---------- Stream info editing ----------
  const startEditField = (field, currentValue) => { setEditingField(field); setEditValue(currentValue || ''); };
  const cancelEditField = () => { setEditingField(null); setEditValue(''); };

  const handleUpdateStreamInfo = async (field) => {
    if (field === 'name' && !editValue.trim()) { alert('名称不能为空'); return; }
    setSavingInfo(true);
    try {
      const params = new URLSearchParams();
      params.append(field, editValue);
      const res = await adminFetch(`/admin/streams/${streamId}/info?${params.toString()}`, { method: 'PUT' });
      if (!res.ok) throw new Error('更新失败');
      const result = await res.json();
      setStreamInfo(prev => ({ ...prev, ...result.stream_info }));
      setEditingField(null);
      setEditValue('');
    } catch (err) { alert('更新失败: ' + err.message); }
    finally { setSavingInfo(false); }
  };

  const handleImageUpload = async (imageType, file) => {
    if (!file) return;
    setUploadingImage(imageType);
    try {
      const formData = new FormData();
      formData.append('image_type', imageType);
      formData.append('file', file);
      const res = await adminFetch(`/admin/streams/${streamId}/upload-image`, { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '上传失败');
      updateStreamInfoState({
        [imageType]: data.filename,
        [`${imageType}_url`]: data.url,
      });
    } catch (err) { alert('上传失败: ' + err.message); }
    finally { setUploadingImage(null); }
  };

  const handleUpdateDisplayOrder = async () => {
    const newOrder = parseInt(orderValue, 10);
    if (isNaN(newOrder)) { alert('请输入有效的数字'); return; }
    try {
      const res = await adminFetch(`/admin/streams/${streamId}/display-order?display_order=${newOrder}`, { method: 'PUT' });
      if (!res.ok) throw new Error('更新失败');
      updateStreamInfoState({ display_order: newOrder });
      setEditingOrder(false);
    } catch (err) { alert('更新排序失败: ' + err.message); }
  };

  if (loading) return <div className="admin-page"><div className="admin-loading">加载中...</div></div>;
  if (error) return <div className="admin-page"><div className="admin-error"><span>加载失败: {error}</span><button onClick={fetchData}>重试</button></div></div>;

  return (
    <div className="admin-page">
      {/* Header */}
      <header className="admin-header">
        <h1>{streamInfo?.name || '互动空间管理'}</h1>
        <button type="button" className="admin-refresh-btn" onClick={fetchData}>刷新</button>
      </header>

      <div className="admin-body">
        {/* ===== 基本信息 ===== */}
        <section className="admin-section">
          <h2 className="section-title">基本信息</h2>
          <div className="info-grid">
            {/* 图片区 */}
            <div className="info-images">
              {[
                { key: 'character_image', label: '角色图片' },
                { key: 'scene_image', label: '场景图片' },
                { key: 'initial_image', label: '初始画面' },
              ].map(item => (
                <div className="info-image-item" key={item.key}>
                  <span className="info-image-label">{item.label}</span>
                  <div className="info-image-box">
                    {(streamInfo?.[`${item.key}_url`] || streamInfo?.[item.key]) ? (
                      <img
                        src={streamInfo[`${item.key}_url`] || `/static/images/${streamInfo[item.key]}`}
                        alt={item.label}
                      />
                    ) : null}
                    <label className={`info-image-overlay ${streamInfo?.[item.key] ? 'has' : ''}`}>
                      {uploadingImage === item.key ? '...' : (streamInfo?.[item.key] ? '换' : '上传')}
                      <input type="file" accept="image/*" onChange={e => handleImageUpload(item.key, e.target.files?.[0])} hidden />
                    </label>
                  </div>
                </div>
              ))}
            </div>
            {/* 文字区 */}
            <div className="info-fields">
              <InfoField label="名称" value={streamInfo?.name} field="name"
                editing={editingField} editValue={editValue} saving={savingInfo}
                onStart={startEditField} onCancel={cancelEditField} onChange={setEditValue} onSave={handleUpdateStreamInfo} />
              <InfoField label="描述" value={streamInfo?.description} field="description" multiline
                editing={editingField} editValue={editValue} saving={savingInfo}
                onStart={startEditField} onCancel={cancelEditField} onChange={setEditValue} onSave={handleUpdateStreamInfo} />
              <InfoField label="角色" value={streamInfo?.character_description} field="character_description" multiline
                editing={editingField} editValue={editValue} saving={savingInfo}
                onStart={startEditField} onCancel={cancelEditField} onChange={setEditValue} onSave={handleUpdateStreamInfo} />
              <div className="info-field-row">
                <span className="info-field-label">排序</span>
                {editingOrder ? (
                  <span className="info-field-edit">
                    <input type="number" value={orderValue} onChange={e => setOrderValue(e.target.value)} autoFocus />
                    <button className="btn-sm btn-primary" onClick={handleUpdateDisplayOrder}>保存</button>
                    <button className="btn-sm" onClick={() => setEditingOrder(false)}>取消</button>
                  </span>
                ) : (
                  <span className="info-field-value clickable" onClick={() => { setOrderValue(String(streamInfo?.display_order ?? 0)); setEditingOrder(true); }}>
                    {streamInfo?.display_order ?? 0}
                  </span>
                )}
              </div>
              <div className="info-field-row">
                <span className="info-field-label">创建状态</span>
                <select
                  className="info-field-value admin-select-status"
                  value={streamInfo?.creation_status || 'completed'}
                  onChange={async (e) => {
                    const v = e.target.value;
                    try {
                      const res = await adminFetch(
                        `/admin/streams/${streamId}/info?${new URLSearchParams({ creation_status: v })}`,
                        { method: 'PUT' }
                      );
                      if (!res.ok) {
                        const errBody = await res.json().catch(() => ({}));
                        const msg = typeof errBody.detail === 'string' ? errBody.detail : '更新失败';
                        throw new Error(msg);
                      }
                      const data = await res.json();
                      setStreamInfo((prev) => ({
                        ...prev,
                        creation_status: data.stream_info?.creation_status ?? v,
                      }));
                    } catch (err) {
                      alert(err.message);
                    }
                  }}
                >
                  {streamInfo?.creation_status
                    && !CREATION_STATUS_OPTIONS.some((o) => o.value === streamInfo.creation_status) ? (
                      <option value={streamInfo.creation_status}>
                        {streamInfo.creation_status}（当前）
                      </option>
                    ) : null}
                  {CREATION_STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="info-field-row info-field-row-checkbox">
                <span className="info-field-label">访客可见</span>
                <label className="info-field-value" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={(streamInfo?.is_public ?? 0) === 1}
                    onChange={async (e) => {
                      const v = e.target.checked ? 1 : 0;
                      try {
                        const res = await adminFetch(`/admin/streams/${streamId}/info?is_public=${v}`, { method: 'PUT' });
                        if (!res.ok) throw new Error('更新失败');
                        const data = await res.json();
                        setStreamInfo((prev) => ({ ...prev, is_public: data.stream_info?.is_public ?? v }));
                      } catch (err) {
                        alert(err.message);
                      }
                    }}
                  />
                  <span>在首页与 Feed 展示（须勾选且为活跃；状态为「已完成」或「已完成（有错误）」）</span>
                </label>
              </div>
            </div>
          </div>
        </section>

        {/* ===== 背景音乐 ===== */}
        <BackgroundMusicSection
          selectedMusic={selectedStreamMusic}
          uploading={uploadingMusic}
          savingSelection={savingStreamMusic}
          onUpload={handleUploadBackgroundMusic}
          onClear={handleClearBackgroundMusic}
        />

        {/* ===== 片头视频 ===== */}
        <section className="admin-section">
          <div className="section-header-row">
            <h2 className="section-title">片头视频 <span className="hint">（可选，首次播放一次）</span></h2>
          </div>
          <div className="special-video-zone">
            {introVideo ? (
              <div className="special-video-card">
                <div className="sv-thumb" onClick={() => setPreviewVideo(introVideo)}>
                  <video src={introVideo.video_url || `/static/videos/${introVideo.filename}`} muted preload="metadata" />
                  <div className="sv-play">▶</div>
                </div>
                <div className="sv-info">
                  <span>{introVideo.interaction_label || '片头'}</span>
                  <div className="sv-actions">
                    <label className={`btn-sm btn-primary ${uploading === `replace_${introVideo.id}` ? 'loading' : ''}`}>
                      {uploading === `replace_${introVideo.id}` ? '替换中...' : '替换视频'}
                      <input type="file" accept="video/*" onChange={e => handleReplaceVideoFile(introVideo.id, e.target.files?.[0])} hidden />
                    </label>
                    <button className="btn-sm btn-danger" onClick={() => handleDeleteVideo(introVideo)}>删除</button>
                  </div>
                  <VideoAudioSettings
                    video={introVideo}
                    selectedMusic={selectedStreamMusic}
                    saving={savingAudioVideoId === introVideo.id}
                    onSave={handleSaveAudioConfig}
                    onPreview={(video, draft) => setPreviewVideo({ video, audioConfig: draft })}
                  />
                </div>
              </div>
            ) : (
              <UploadZone label="上传片头视频" loading={uploading === 'intro'} onFile={f => handleUploadVideo('intro', f)} />
            )}
          </div>
        </section>

        {/* ===== 待机视频 ===== */}
        <section className="admin-section">
          <div className="section-header-row">
            <h2 className="section-title">待机视频 <span className="hint">（无互动时循环播放）</span></h2>
          </div>
          <div className="special-video-zone">
            {idleVideo ? (
              <div className="special-video-card">
                <div className="sv-thumb" onClick={() => setPreviewVideo(idleVideo)}>
                  <video src={idleVideo.video_url || `/static/videos/${idleVideo.filename}`} muted preload="metadata" />
                  <div className="sv-play">▶</div>
                </div>
                <div className="sv-info">
                  <span>{idleVideo.interaction_label || '待机'}</span>
                  <div className="sv-actions">
                    {idleVideo.video_category !== 'idle' && (
                      <button className="btn-sm btn-primary" onClick={() => handleSetIdleVideo(idleVideo.id)}>设为待机</button>
                    )}
                    <label className={`btn-sm btn-primary ${uploading === `replace_${idleVideo.id}` ? 'loading' : ''}`}>
                      {uploading === `replace_${idleVideo.id}` ? '替换中...' : '替换视频'}
                      <input type="file" accept="video/*" onChange={e => handleReplaceVideoFile(idleVideo.id, e.target.files?.[0])} hidden />
                    </label>
                    <button className="btn-sm btn-danger" onClick={() => handleDeleteVideo(idleVideo)}>删除</button>
                  </div>
                  <VideoAudioSettings
                    video={idleVideo}
                    selectedMusic={selectedStreamMusic}
                    saving={savingAudioVideoId === idleVideo.id}
                    onSave={handleSaveAudioConfig}
                    onPreview={(video, draft) => setPreviewVideo({ video, audioConfig: draft })}
                  />
                </div>
              </div>
            ) : (
              <UploadZone label="上传待机视频" loading={uploading === 'idle'} onFile={f => handleUploadVideo('idle', f)} />
            )}
          </div>
        </section>

        {/* ===== 互动视频 ===== */}
        <section className="admin-section">
          <div className="section-header-row">
            <h2 className="section-title">互动视频 <span className="hint">（共 {interactionVideos.length} 个，拖拽调整播放顺序）</span></h2>
            <label className="btn-sm btn-primary upload-btn-inline">
              + 上传视频
              <input type="file" accept="video/*" onChange={e => handleUploadVideo('interaction', e.target.files?.[0])} hidden />
            </label>
          </div>

          {interactionVideos.length === 0 ? (
            <div className="empty-hint">暂无互动视频，点击上方按钮添加</div>
          ) : (
            <div className="interaction-list">
              {interactionVideos.map((video, idx) => (
                <div
                  key={video.id}
                  className={`interaction-item ${draggingId === video.id ? 'dragging' : ''}`}
                  draggable
                  onDragStart={() => handleDragStart(video.id)}
                  onDragOver={e => handleDragOver(e, video.id)}
                  onDrop={handleDrop}
                  onDragEnd={() => setDraggingId(null)}
                >
                  <div className="ii-drag-handle" title="拖拽排序">⠿</div>
                  <div className="ii-order">{idx + 1}</div>
                  <div className="ii-thumb" onClick={() => setPreviewVideo(video)}>
                    <video src={video.video_url || `/static/videos/${video.filename}`} muted preload="metadata" />
                    <div className="sv-play">▶</div>
                  </div>
                  <div className="ii-meta">
                    <div className="ii-label">
                      <span className="ii-emoji">{video.emoji || '🎭'}</span>
                      <span className="ii-name">{video.interaction_label || video.interaction_type}</span>
                    </div>
                    <div
                      className={`ii-mode-toggle ${video.generation_mode === 'realtime' ? 'realtime' : 'preset'}`}
                      onClick={() => handleUpdateVideo(video.id, { generation_mode: video.generation_mode === 'realtime' ? 'preset' : 'realtime' })}
                      title="点击切换：预制视频 / 实时生成"
                    >
                      <span className="mode-dot" />
                      <span className="mode-text">{video.generation_mode === 'realtime' ? '实时生成' : '预制视频'}</span>
                    </div>
                    {video.action_image && (
                      <img className="ii-action-img" src={video.action_image_url || `/static/images/${video.action_image}`} alt="动作图" />
                    )}
                  </div>
                  <div className="ii-actions">
                    <label className={`btn-sm btn-primary ${uploading === `replace_${video.id}` ? 'loading' : ''}`} title="替换视频文件">
                      {uploading === `replace_${video.id}` ? '替换中...' : '替换视频'}
                      <input type="file" accept="video/*" onChange={e => handleReplaceVideoFile(video.id, e.target.files?.[0])} hidden />
                    </label>
                    <button className="btn-sm" onClick={() => setEditingVideo(video)} title="编辑">✏️</button>
                    <label className="btn-sm" title="上传动作图">
                      🖼️
                      <input type="file" accept="image/*" onChange={e => handleUploadActionImage(video.id, e.target.files?.[0])} hidden />
                    </label>
                    <button className="btn-sm btn-danger" onClick={() => handleDeleteVideo(video)} title="删除">🗑️</button>
                  </div>
                  <VideoAudioSettings
                    compact
                    video={video}
                    selectedMusic={selectedStreamMusic}
                    saving={savingAudioVideoId === video.id}
                    onSave={handleSaveAudioConfig}
                    onPreview={(targetVideo, draft) => setPreviewVideo({ video: targetVideo, audioConfig: draft })}
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* ===== Modals ===== */}
      {previewVideo && (
        <VideoPreviewModal
          video={previewVideo.video || previewVideo}
          audioConfig={previewVideo.audioConfig}
          selectedMusic={selectedStreamMusic}
          onClose={() => setPreviewVideo(null)}
        />
      )}
      {editingVideo && <VideoEditModal video={editingVideo} onSave={handleUpdateVideo} onClose={() => setEditingVideo(null)} />}
    </div>
  );
};

/* ---- Helper Components ---- */

const InfoField = ({ label, value, field, multiline, editing, editValue, saving, onStart, onCancel, onChange, onSave }) => (
  <div className="info-field-row">
    <span className="info-field-label">{label}</span>
    {editing === field ? (
      <span className="info-field-edit">
        {multiline ? (
          <textarea value={editValue} onChange={e => onChange(e.target.value)} rows={2} autoFocus disabled={saving} />
        ) : (
          <input type="text" value={editValue} onChange={e => onChange(e.target.value)} autoFocus disabled={saving} />
        )}
        <button className="btn-sm btn-primary" onClick={() => onSave(field)} disabled={saving}>{saving ? '...' : '保存'}</button>
        <button className="btn-sm" onClick={onCancel} disabled={saving}>取消</button>
      </span>
    ) : (
      <span className="info-field-value clickable" onClick={() => onStart(field, value)}>
        {value || <em className="no-val">未设置</em>}
      </span>
    )}
  </div>
);

const UploadZone = ({ label, loading, onFile }) => (
  <label className={`upload-zone ${loading ? 'loading' : ''}`}>
    {loading ? (
      <span className="upload-zone-loading">
        <span className="upload-zone-spinner" />
        <span>上传中...</span>
      </span>
    ) : (
      <span>{label}</span>
    )}
    <input type="file" accept="video/*" onChange={e => onFile(e.target.files?.[0])} hidden disabled={loading} />
  </label>
);

const BackgroundMusicSection = ({ selectedMusic, uploading, savingSelection, onUpload, onClear }) => {
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  const togglePreview = (music) => {
    if (!music.audio_url) {
      alert('该背景音乐暂无可播放地址');
      return;
    }
    if (playingId === music.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = music.audio_url;
      audioRef.current.volume = 0.6;
      audioRef.current.play().then(() => setPlayingId(music.id)).catch(() => {
        setPlayingId(null);
        alert('试听失败，请点击页面后重试');
      });
    }
  };

  return (
    <section className="admin-section bgm-section">
      <div className="section-header-row">
        <h2 className="section-title">背景音乐 <span className="hint">（整个互动空间共用同一首，视频只调节各自 BGM 音量）</span></h2>
      </div>
      <div className="special-video-zone">
        {selectedMusic ? (
          <div className="special-video-card bgm-selected-card">
            <div className="sv-thumb bgm-thumb" onClick={() => togglePreview(selectedMusic)}>
              <div className="bgm-thumb-inner">
                <span className="bgm-thumb-icon">♪</span>
              </div>
              <div className="sv-play">{playingId === selectedMusic.id ? '❚❚' : '▶'}</div>
            </div>
            <div className="sv-info">
              <span>{selectedMusic.title}</span>
              <small className="bgm-card-desc">
                {selectedMusic.filename || '已绑定到当前互动空间'}
              </small>
              <div className="sv-actions">
                <button type="button" className="btn-sm" onClick={() => togglePreview(selectedMusic)}>
                  {playingId === selectedMusic.id ? '暂停试听' : '播放试听'}
                </button>
                <label className={`btn-sm btn-primary ${uploading ? 'loading' : ''}`}>
                  {uploading ? '替换中...' : '替换音乐'}
                  <input
                    type="file"
                    accept="audio/*"
                    onChange={e => onUpload(e.target.files?.[0] || null)}
                    hidden
                    disabled={uploading || savingSelection}
                  />
                </label>
                <button
                  type="button"
                  className="btn-sm"
                  onClick={onClear}
                  disabled={savingSelection || uploading}
                >
                  {savingSelection ? '保存中...' : '取消绑定'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <label className={`upload-zone bgm-upload-zone ${uploading ? 'loading' : ''}`}>
            {uploading ? (
              <span className="upload-zone-loading">
                <span className="upload-zone-spinner" />
                <span>上传中...</span>
              </span>
            ) : (
              <span>上传背景音乐</span>
            )}
            <input
              type="file"
              accept="audio/*"
              onChange={e => onUpload(e.target.files?.[0] || null)}
              hidden
              disabled={uploading || savingSelection}
            />
          </label>
        )}
      </div>
      <audio ref={audioRef} onEnded={() => setPlayingId(null)} />
    </section>
  );
};

const VideoAudioSettings = ({ video, selectedMusic, saving, onSave, onPreview, compact = false }) => {
  const [draft, setDraft] = useState(() => getVideoAudioConfig(video));
  const lastSavedVideoRef = useRef(null);

  useEffect(() => {
    if (saving) return;
    setDraft(getVideoAudioConfig(video));
  }, [saving, video]);

  useEffect(() => {
    if (saving) {
      lastSavedVideoRef.current = video.id;
      return;
    }
    if (lastSavedVideoRef.current === video.id) {
      lastSavedVideoRef.current = null;
    }
  }, [saving, video.id]);

  const updateDraft = (patch) => setDraft(prev => ({ ...prev, ...patch }));

  return (
    <div className={`video-audio-settings ${compact ? 'compact' : ''}`}>
      <div className="audio-row">
        <span>原视频音量 {draft.video_volume}%</span>
        <input
          type="range"
          min="0"
          max="100"
          value={draft.video_volume}
          onChange={e => updateDraft({ video_volume: clampVolume(e.target.value, 100) })}
        />
      </div>
      <div className="audio-row">
        <span>背景音乐音量 {draft.background_music_volume}%</span>
        <input
          type="range"
          min="0"
          max="100"
          value={draft.background_music_volume}
          onChange={e => updateDraft({ background_music_volume: clampVolume(e.target.value, 30) })}
        />
      </div>
      {!selectedMusic && (
        <div className="audio-warning">当前互动空间尚未选择背景音乐，预览时只播放原视频</div>
      )}
      <div className="audio-actions">
        <button type="button" className="btn-sm" onClick={() => onPreview(video, draft)}>
          预览音频效果
        </button>
        <button type="button" className="btn-sm btn-primary" onClick={() => onSave(video, draft)} disabled={saving}>
          {saving ? '保存中...' : '保存音频设置'}
        </button>
      </div>
    </div>
  );
};

const VideoPreviewModal = ({ video, audioConfig, selectedMusic, onClose }) => {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const config = audioConfig || getVideoAudioConfig(video);
  const bgmUrl = selectedMusic?.audio_url || video.background_music_audio_url;
  const shouldPlayBgm = Boolean(bgmUrl);
  const videoVolume = clampVolume(config.video_volume, 100);
  const bgmVolume = clampVolume(config.background_music_volume, 30);

  useEffect(() => {
    const videoEl = videoRef.current;
    const audioEl = audioRef.current;
    if (videoEl) videoEl.volume = videoVolume / 100;
    if (audioEl) audioEl.volume = bgmVolume / 100;
  }, [videoVolume, bgmVolume]);

  useEffect(() => () => {
    videoRef.current?.pause();
    audioRef.current?.pause();
  }, []);

  const playBgm = () => {
    if (!shouldPlayBgm || !audioRef.current) return;
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(() => {});
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="preview-modal-content" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        <video
          ref={videoRef}
          src={video.video_url || `/static/videos/${video.filename}`}
          controls
          autoPlay
          loop
          className="preview-video"
          onPlay={playBgm}
          onPause={() => audioRef.current?.pause()}
          onEnded={() => {
            if (audioRef.current) {
              audioRef.current.pause();
              audioRef.current.currentTime = 0;
            }
          }}
        />
        {shouldPlayBgm && (
          <audio ref={audioRef} src={bgmUrl} loop />
        )}
        <div className="preview-info">
          <span className="preview-emoji">{video.emoji}</span>
          <span className="preview-label">{video.interaction_label || video.interaction_type}</span>
        </div>
        <div className="preview-audio-info">
          <span>视频音量 {videoVolume}%</span>
          <span>{shouldPlayBgm ? `BGM：${selectedMusic?.title || video.background_music_title || '背景音乐'} · ${bgmVolume}%` : '未启用背景音乐'}</span>
        </div>
      </div>
    </div>
  );
};

const VideoEditModal = ({ video, onSave, onClose }) => {
  const [form, setForm] = useState({
    interaction_label: video.interaction_label || '',
    emoji: video.emoji || '',
    interaction_type: video.interaction_type || '',
    video_category: video.video_category || 'interaction',
    priority: video.priority || 0,
    video_prompt: video.video_prompt || '',
    generation_mode: video.generation_mode || 'preset'
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const ok = await onSave(video.id, form);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>编辑视频</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>标签</label>
            <input type="text" value={form.interaction_label} onChange={e => setForm({ ...form, interaction_label: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Emoji</label>
            <input type="text" value={form.emoji} onChange={e => setForm({ ...form, emoji: e.target.value })} maxLength={4} />
          </div>
          <div className="form-group">
            <label>类型标识</label>
            <input type="text" value={form.interaction_type} onChange={e => setForm({ ...form, interaction_type: e.target.value })} />
          </div>
          <div className="form-group">
            <label>分类</label>
            <select value={form.video_category} onChange={e => setForm({ ...form, video_category: e.target.value })}>
              <option value="intro">片头</option>
              <option value="idle">待机</option>
              <option value="interaction">互动</option>
              <option value="basic">基础</option>
              <option value="public">公开</option>
              <option value="private">私人</option>
              <option value="story">故事</option>
            </select>
          </div>
          <div className="form-group">
            <label>互动模式</label>
            <select value={form.generation_mode} onChange={e => setForm({ ...form, generation_mode: e.target.value })}>
              <option value="preset">预制视频</option>
              <option value="realtime">实时生成</option>
            </select>
          </div>
          <div className="form-group">
            <label>Prompt（实时生成时使用）</label>
            <textarea value={form.video_prompt} onChange={e => setForm({ ...form, video_prompt: e.target.value })} rows={3} style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
          <div className="form-group">
            <label>优先级</label>
            <input type="number" value={form.priority} onChange={e => setForm({ ...form, priority: parseInt(e.target.value) || 0 })} />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>取消</button>
            <button type="submit" className="btn-save" disabled={saving}>{saving ? '...' : '保存'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StreamAdminPage;
